import { Check } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * Shared selectable-row control for both calculators. Matches Stitch's
 * `services_pricing` checkbox-row pattern (circular check target + name +
 * right-aligned price) merged with the current site's `.calc-addon` pill
 * treatment (GlassCard-style border, price-standalone strikethrough).
 * Selected rows glow warm (fire), matching every other "selected" state
 * across the site — cyan is reserved for the bundle-savings numbers only.
 */
export function AddonRow({
  active,
  onToggle,
  icon,
  name,
  note,
  price,
  standalonePrice,
  priceSuffix,
}: {
  active: boolean
  onToggle: () => void
  icon: ReactNode
  name: string
  note: string
  price: number
  standalonePrice?: number
  priceSuffix?: string
}) {
  return (
    <div
      role="checkbox"
      aria-checked={active}
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault()
          onToggle()
        }
      }}
      className={`relative flex cursor-pointer items-start gap-4 rounded-lg border p-4 transition-all duration-normal ${
        active
          ? 'glow-ring border-accent-fire/50 bg-accent-fire/10'
          : 'border-white/5 bg-white/[0.03] hover:border-white/15 hover:bg-white/[0.06]'
      }`}
    >
      <div
        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${
          active ? 'border-accent-fire bg-accent-fire/20' : 'border-white/25'
        }`}
      >
        <Check
          size={14}
          strokeWidth={3}
          className={`text-accent-fire transition-transform duration-fast ${active ? 'scale-100' : 'scale-0'}`}
        />
      </div>

      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-foreground-muted">
        {icon}
      </div>

      {/* Full-width text column, price moved below name/note instead of a
          right-aligned shrink-0 column — the old side-by-side layout left
          too little width for name/note on narrow screens, wrapping into
          2-3 words per line. */}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-foreground">{name}</div>
        <div className="mt-0.5 text-xs text-foreground-muted">{note}</div>
        <div className="mt-2">
          {standalonePrice !== undefined && (
            <span className="mr-1.5 text-xs text-foreground-subtle line-through">{standalonePrice} EUR</span>
          )}
          <span className={`text-sm font-bold ${active ? 'text-accent-fire' : 'text-accent-amber-light'}`}>
            +{price} {priceSuffix ?? 'EUR'}
          </span>
        </div>
      </div>
    </div>
  )
}
